/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Service, CreateServiceRequest } from "@/api/services";
import { IconPicker } from "./IconPicker";

interface ServiceFormProps {
  service?: Service | null;
  onSubmit: (data: CreateServiceRequest) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  isOpen: boolean;
}

const emptyForm = (): CreateServiceRequest => ({
  name: "",
  url: "",
  iconType: "preset",
  iconValue: "",
  groupName: "",
  enabled: true,
  showLabel: true,
});

export const ServiceForm: React.FC<ServiceFormProps> = ({
  service,
  onSubmit,
  onCancel,
  isSubmitting,
  isOpen,
}) => {
  const [formData, setFormData] = useState<CreateServiceRequest>(emptyForm());

  useEffect(() => {
    if (service) {
      setFormData({
        name: service.name,
        url: service.url,
        iconType: service.iconType,
        iconValue: service.iconValue || "",
        groupName: service.groupName || "",
        enabled: service.enabled,
        showLabel: service.showLabel,
      });
    } else if (isOpen) {
      setFormData(emptyForm());
    }
  }, [service, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      name: formData.name.trim(),
      url: formData.url.trim(),
      groupName: formData.groupName?.trim() || undefined,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onCancel}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{service ? "Edit Service" : "Add Service"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="service-name">Name</Label>
            <Input
              id="service-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Plex"
              required
            />
          </div>

          <div>
            <Label htmlFor="service-url">URL</Label>
            <Input
              id="service-url"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              placeholder="http://192.168.1.10:32400"
              required
            />
          </div>

          <div>
            <Label htmlFor="service-group">Group (optional)</Label>
            <Input
              id="service-group"
              value={formData.groupName}
              onChange={(e) => setFormData({ ...formData, groupName: e.target.value })}
              placeholder="Media"
            />
          </div>

          <div>
            <Label>Icon</Label>
            <IconPicker
              iconType={formData.iconType}
              iconValue={formData.iconValue || ""}
              onChange={(iconType, iconValue) =>
                setFormData({ ...formData, iconType, iconValue })
              }
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="service-enabled"
              checked={formData.enabled}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, enabled: checked as boolean })
              }
            />
            <Label htmlFor="service-enabled" className="cursor-pointer">
              Show on Home tab
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="service-show-label"
              checked={formData.showLabel}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, showLabel: checked as boolean })
              }
            />
            <Label htmlFor="service-show-label" className="cursor-pointer">
              Show name on tile
            </Label>
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="default" disabled={isSubmitting} isLoading={isSubmitting}>
              {service ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
